import axios from 'axios';
import Environment from "../utils/Environment";
import { parseErrorMessages } from "../utils/ErrorUtils";
import { 
  transformAndFilterRows, 
  validateIN02LoadGroups, 
  prepareFormData, 
  getApiConfig,
  RowData
} from './JsonProcessingUtils';

/**
 * Handles JSON validation for different tab types (XDOCK, IN02, etc.)
 * 
 * IMPORTANT: For IN02 validation, the field names in the JSON data must match exactly 
 * with the @Parsed(field = "...") annotations in the backend LoadGroupBean.java DTO.
 * 
 * @param selectedDC - The selected distribution center
 * @param tabType - Tab type (IN02 or XDOCK)
 * @param setIsJsonValidated - Function to set validation status
 * @param rows - Array of row data to validate
 * @param setIsLoading - Function to set loading status
 * @param showSuccess - Function to show success toast messages
 * @param showError - Function to show error toast messages
 */
const handleJsonValidation = async (
  selectedDC: string,
  tabType: string,
  setIsJsonValidated: (value: boolean) => void,
  rows: RowData[],
  setIsLoading: (value: boolean) => void,
  showSuccess: (message: string) => void,
  showError: (message: string, fullErrors?: string[]) => void,

) => {
  // Transform and filter rows using shared utility
  const filteredRows = transformAndFilterRows(rows, tabType);

  // Validate Load Group fields for IN02 tab type
  if (tabType === 'IN02') {
    const validation = validateIN02LoadGroups(filteredRows, selectedDC, Environment);
    if (!validation.isValid) {
      setIsLoading(false);
      setIsJsonValidated(false);
      showError(validation.errorMessage!);
      return;
    }
  }

  // Prepare form data for API submission
  const data = prepareFormData(filteredRows, selectedDC);
  
  // Get validation API configuration based on selected DC and tab type
  const { apiEndpoint: jsonValidationAPI, fileTypeLabel } = getApiConfig(tabType, 'validation', Environment, selectedDC);
  
  console.log('DEBUG - handleJsonValidation:', { selectedDC, tabType, jsonValidationAPI, fileTypeLabel });
  
  setIsLoading(true);
  try {
    
    const response = await axios.post(jsonValidationAPI || '', data);
    console.log('Response:', response);
    if (response.status === 200) {
      setIsJsonValidated(true);
      showSuccess(`${fileTypeLabel} Data Validated successfully - No Errors found`);
    } else {
      setIsJsonValidated(false);
      const errorMessage = `${fileTypeLabel} Data Validation failed: ` + response.data;
      const parsedErrors = parseErrorMessages(response.data.message || response.data);
 // Pass parsed errors if there are multiple error lines or if parsing found multiple errors
      const shouldShowFullErrors = parsedErrors.length > 1 || parsedErrors.some(e => 
        /^Error.*line/i.test(e) || 
        /error.*line/i.test(e) || 
        /^.*\.(csv|txt):/i.test(e)
      );
      if (shouldShowFullErrors) {
        showError(errorMessage, parsedErrors);
      } else {
        showError(errorMessage);
      }
    }
  } catch (err: unknown) {
    console.log('Error:', err);
    setIsJsonValidated(false);
    
    const error = err as { response?: { data: string }; request?: unknown; message?: string };
    
    if (error.response) {
       const errorMessage = `${fileTypeLabel} Validation failed: ` + error.response.data;
      const parsedErrors = parseErrorMessages(error.response.data);
      // Pass parsed errors if there are multiple error lines or if parsing found multiple errors
      const shouldShowFullErrors = parsedErrors.length > 1 || parsedErrors.some(e => 
        /^Error.*line/i.test(e) || 
        /error.*line/i.test(e) || 
        /^.*\.(csv|txt):/i.test(e)
      );
      if (shouldShowFullErrors) {
        showError(errorMessage, parsedErrors);
      } else {
        showError(errorMessage);
      }
    } else if (error.request) {
      showError(`${fileTypeLabel} Data Validation failed: No response received`);
    } else {
      showError(`${fileTypeLabel} Data Validation failed: ` + (error.message || 'Unknown error'));
    }
  } finally {
    setIsLoading(false);
  }
};

export const HandleJSONValidation = handleJsonValidation;
export default handleJsonValidation;
